import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, isPast: false });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const difference = target - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, isPast: true };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

      return { days, hours, minutes, isPast: false };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [targetDate]);

  if (timeLeft.isPast) {
    return (
      <div className="inline-flex items-center gap-2 text-sm text-[#6C757D] bg-[#F9F8F6] rounded-full px-4 py-2">
        <Calendar className="w-4 h-4" />
        <span>Event Completed</span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-3 text-sm text-[#6C757D] bg-[#F9F8F6] rounded-full px-4 py-2">
      <Calendar className="w-4 h-4 text-[#D4A373]" />
      <span className="font-medium">
        {timeLeft.days}d {timeLeft.hours}h remaining
      </span>
    </div>
  );
};

// Retreat Card Component
const RetreatCard = ({ retreat, index, isReversed }) => {
  const contentOrder = isReversed ? 'md:order-2' : 'md:order-1';
  const imageOrder = isReversed ? 'md:order-1' : 'md:order-2';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="bg-white rounded-3xl shadow-lg overflow-hidden"
      data-testid={`retreat-${retreat.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Content */}
        <div className={`p-8 md:p-12 flex flex-col justify-center ${contentOrder}`}>
          <p className="text-sm uppercase tracking-widest font-bold text-[#D4A373] mb-3">
            {retreat.location ? `${retreat.location} \u2022 ` : ''}{retreat.date}
          </p>
          
          {retreat.targetDate && (
            <div className="mb-4">
              <CountdownTimer targetDate={retreat.targetDate} />
            </div>
          )}

          <h3 className="font-serif text-3xl md:text-4xl font-semibold text-[#2C332B] mb-2">
            {retreat.title}
          </h3>

          {retreat.subtitle && (
            <p className="text-lg text-[#D4A373] font-medium mb-4">
              {retreat.subtitle}
            </p>
          )}

          <div className="space-y-4 text-[#6C757D] leading-relaxed mb-6">
            {retreat.description.map((para, idx) => (
              <p key={idx}>{para}</p>
            ))}
          </div>

          {retreat.includes && (
            <div className="mb-6">
              <p className="font-semibold text-[#2C332B] mb-3">{retreat.priceText}</p>
              <ul className="space-y-2">
                {retreat.includes.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#6C757D]">
                    <span className="text-[#D4A373] mt-1">{'\u2713'}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {retreat.deposit && (
            <p className="text-[#3A4A3F] font-medium mb-6">
              {retreat.deposit}
            </p>
          )}

          {retreat.additionalContent && (
            <div className="mb-6">
              <h4 className="font-serif text-xl font-semibold text-[#2C332B] mb-2">
                {retreat.additionalContent.title}
              </h4>
              {retreat.additionalContent.subtitle && (
                <p className="text-[#D4A373] font-medium italic mb-3">
                  {retreat.additionalContent.subtitle}
                </p>
              )}
              <div className="space-y-3 text-[#6C757D] leading-relaxed">
                {retreat.additionalContent.paragraphs.map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>
          )}

          <a
            href={retreat.bookingUrl}
            className={`inline-flex items-center gap-2 px-8 py-4 text-lg font-medium rounded-full transition-all duration-300 hover:scale-[1.02] self-start ${
              retreat.isWaitingList
                ? 'bg-[#D4A373] text-white hover:bg-[#C69363]'
                : 'bg-[#3A4A3F] text-white hover:bg-[#2C332B]'
            }`}
            data-testid={`book-${retreat.id}`}
          >
            {retreat.isWaitingList && <Mail className="w-5 h-5" />}
            {retreat.buttonText || 'Book Your Place'}
            {!retreat.isWaitingList && <ArrowRight className="w-5 h-5" />}
          </a>
        </div>

        {/* Image */}
        <div className={`relative h-[300px] md:h-auto min-h-[400px] ${imageOrder}`}>
          <img
            src={retreat.image}
            alt={retreat.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
    </motion.div>
  );
};

export const Book = () => {
  useEffect(() => {
    document.title = 'Book Your Retreat - Wild Thyme Women\'s Wellbeing Retreats';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Reserve your place at a nourishing women\'s retreat. Weekend and midweek options in beautiful UK locations. All meals, accommodation, and gentle activities included.';
  }, []);

  const retreats = [
    {
      id: 'wild-swimming',
      title: 'Wild Swimming',
      subtitle: 'One Day Introduction & Hike',
      date: '19th September 2026',
      targetDate: '2026-09-19T09:00:00',
      image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/8i5xssdc_Wild%20Swim.jpeg',
      bookingUrl: 'https://147723757.hs-sites-eu1.com/wild-swim-sept26',
      description: [
        'A guided introduction to wild swimming and hiking in the stunning Pennines. Join us for a day of adventure, fun, connection and expanding your comfort zone.',
        'Qualified open water swim & hike leaders will guide you through inland swims in beautiful natural locations, combined with relaxed hikes to access these stunning spots. This is all about spending time with like-minded women, enjoying time out.',
      ],
    },
    {
      id: 'coastal-adventure',
      title: 'Coastal Adventure',
      subtitle: 'Wild Swim & Walk',
      date: '9th \u2013 11th October 2026',
      targetDate: '2026-10-09T13:00:00',
      image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/ungb0sa1_Coastel%20Wild%20Swim.jpeg',
      bookingUrl: 'https://147723757.hs-sites-eu1.com/wild-swim-walk-oct-26',
      description: [
        'Swim in the ever-changing waters of the sea. Wander across sweeping moorland and dramatic clifftop paths before warming tired muscles in a wood-fired wild sauna.',
        'This coastal adventure combines the thrill of open water sea swimming with stunning clifftop walks and the indulgence of a wood-fired wild sauna. Spend your days exploring dramatic coastline, connecting with nature and building lasting friendships with like-minded women.',
      ],
    },
    {
      id: 'samhain-fire-walk',
      title: 'Samhain Fire Walk',
      subtitle: 'Step Into Your Power',
      date: 'Thursday 29th October 2026',
      location: 'Hope Valley',
      targetDate: '2026-10-29T18:00:00',
      image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/6ja6780e_FireWalk.png',
      bookingUrl: 'https://147723757.hs-sites-eu1.com/wild-women-firewalk',
      description: [
        'Step towards the glowing embers and leave behind what no longer serves you. An unforgettable evening of fire, connection and discovering what you\u2019re truly capable of.',
        'Inspired by Samhain and the ancient traditions of endings and new beginnings, this powerful fire walk experience invites you to step beyond your comfort zone and embrace change with courage and intention. Share an extraordinary evening with like-minded women, good food and the warmth of the fire as you ignite your inner flame and step forward feeling empowered.',
      ],
      priceText: '\u00a360 per person \u2014 including all activities and supper',
      includes: [
        'The Beaches, Hope Valley, S33 0DA',
        'Thursday 29th October 2026, 6pm\u201310pm',
        'Limited to 20 places',
      ],
    },
    {
      id: 'wild-women',
      title: 'Wild Women',
      subtitle: 'Rise & Reclaim',
      date: '20th \u2013 22nd November 2026',
      location: 'Slaithwaite',
      targetDate: '2026-11-20T14:00:00',
      image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/9thxhl7a_2Wild%20Women.jpeg',
      bookingUrl: 'https://147723757.hs-sites-eu1.com/rise-reclaim',
      description: [
        'This is a women\'s-only weekend retreat designed to empower, restore, and reconnect, running from Friday afternoon to Sunday lunchtime.',
        'Reconnect with your inner wisdom, awaken your feminine power and remember the woman you came here to be. Nourishing food, beautiful accommodation, and a supportive space where you\'ll take part in transformational activities that help you release what\'s holding you back, build inner strength, and step boldly into the life you want.',
      ],
    },
    {
      id: 'read-and-feed',
      title: 'Read & Feed',
      date: 'Coming Soon',
      image: 'https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/3thh1nmc_Read%20and%20Feed2.jpeg',
      bookingUrl: 'mailto:team@wildthyme.co.uk?subject=Read%20%26%20Feed%20Waiting%20List',
      buttonText: 'Join the Waiting List',
      isWaitingList: true,
      description: [
        'Take a whole weekend... just for you.',
        'Imagine stepping away from the noise of everyday life and into a cosy, relaxed space where the only thing on your "to do" list is to rest, read, and reconnect.',
        'Spend your days curled up with your favourite book \u2014 or discover something new from our handpicked collection. No interruptions. No rushing. Just uninterrupted you time.',
        'As evening falls, join our sommelier for a beautiful wine tasting experience, celebrating the flavours of Yorkshire food. Slow sips, warm conversation, and the simple joy of being present.',
        'Whether you\'re craving a quiet corner to escape into your story, or you\'d love to connect with fellow book lovers and share thoughtful conversation, this retreat is for you.',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-32" data-testid="book-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F9F8F6]" data-testid="book-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Join Us
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-[#2C332B] mb-8">
              Book Your Retreat
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D]">
              Take the first step toward reconnection, rest, and renewal
            </p>
          </motion.div>
        </div>
      </section>


      {/* Retreats */}
      <section className="py-24 md:py-32" data-testid="retreats-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {retreats.map((retreat, index) => (
              <RetreatCard
                key={retreat.id}
                retreat={retreat}
                index={index}
                isReversed={index % 2 === 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Experience Types */}
      <section className="py-16 md:py-24 bg-[#E8E4DD]" data-testid="experience-types-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Our Experiences
            </p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#2C332B] mb-6">
              Choose the Experience That Fits You
            </h2>
            <div className="text-lg leading-relaxed text-[#6C757D] max-w-3xl mx-auto space-y-4">
              <p>
                We know that everyone has different amounts of time, budgets and commitments, which is why we offer a range of experiences to make it easier to reconnect with nature in a way that works for you. Whether you're ready to immerse yourself in a full weekend retreat or simply looking for a restorative day outdoors, there's an option to suit you.
              </p>
              <p>
                Please note that not every retreat offers all of these experiences, so be sure to check the individual event details before booking.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'The Day Escape',
                price: 'Prices from \u00a345 per person',
                description: "A wonderful introduction to our retreats and activities. Perfect if you're short on time. Spend the day exploring beautiful landscapes, enjoying guided activities and sharing delicious homemade food before returning home refreshed, restored and inspired.",
                duration: 'One day',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/vpuormpe_FB_IMG_1745789334766.jpg',
              },
              {
                title: 'The Overnight Experience',
                price: 'From \u00a3175 per person',
                description: "Ideal if you can't make a full weekend but still want to experience the magic of an overnight escape. Join us on Saturday morning for a full day of adventure, shared meals and connection before settling in for the night and enjoying a relaxed Sunday morning before heading home.",
                duration: 'Saturday morning to Sunday lunchtime',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/fef339tf_PXL_20250817_181526137.jpg',
              },
              {
                title: 'The Complete Retreat',
                price: 'From \u00a3300 per person',
                description: 'Our signature weekend experience gives you time to fully unwind, slow down and immerse yourself in the rhythm of the outdoors. Guided wild swims, walks, nourishing food, accommodation and plenty of time to relax create the perfect opportunity to reset and reconnect.',
                duration: 'Friday lunchtime to Sunday lunchtime',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/o7uorl3a_walk%20to%20snails.jpg',
              },
            ].map((type, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden shadow-lg"
                data-testid={`experience-type-${idx}`}
              >
                <div className="relative h-64">
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-2xl font-semibold text-[#2C332B] mb-2">
                    {type.title}
                  </h3>
                  <p className="text-lg font-bold text-[#D4A373] mb-4">{type.price}</p>
                  <p className="text-[#6C757D] leading-relaxed mb-6">
                    {type.description}
                  </p>
                  <p className="text-sm font-medium text-[#2C332B]">
                    Typical duration: <span className="text-[#6C757D]">{type.duration}</span>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#E8E4DD]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C332B] mb-6">
              Have Questions?
            </h2>
            <p className="text-lg text-[#6C757D] mb-8">
              We'd love to hear from you. Reach out and we'll respond within 24 hours.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-10 py-5 text-lg font-medium bg-[#3A4A3F] text-white rounded-full hover:bg-[#2C332B] transition-all duration-300 hover:scale-[1.02]"
              data-testid="contact-cta"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
